/**
 * NeoCorePresaCrediticiaPage — leads del funnel neocoop.gt/credito
 * (campaña "Decinos cuánto necesitás" · presa crediticia).
 *
 * Pensada para monitorear la pauta en vivo: KPIs arriba, breakdown por
 * propósito/urgencia/fuente, tabla con link directo a WhatsApp y export
 * Excel. Auto-refresh cada 60s.
 *
 * Fuente: tabla `lead_credito` (migración 0152). RLS: is_admin_or_contador.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  ArrowsClockwise,
  DownloadSimple,
  Funnel,
  MagnifyingGlass,
} from '@phosphor-icons/react'
import { supabase } from '@/lib/supabase'
import { formatQ } from '@/lib/format'
import { exportarExcel } from '@/lib/exportReport'
import { fetchConfigReporte } from '@/lib/useConfigReporte'
import { fechaGT, hoyGT } from '@/lib/fechas'

type LeadRow = {
  id: string
  telefono: string
  nombre: string | null
  monto_deseado: number
  proposito: string
  urgencia: string
  como_resuelve_hoy: string | null
  posicion: number
  referidos_count: number
  utm_source: string | null
  utm_medium: string | null
  utm_campaign: string | null
  utm_content: string | null
  estado: string
  score_declarado: number | null
  created_at: string
}

const PROPOSITO_LABEL: Record<string, string> = {
  negocio: 'Negocio',
  emergencia: 'Emergencia',
  deuda: 'Deuda',
  estudios: 'Estudios',
  casa: 'Casa',
  otro: 'Otro',
}

const URGENCIA_LABEL: Record<string, string> = {
  ya: 'Ya',
  este_mes: 'Este mes',
  mas_adelante: 'Más adelante',
}

const RESUELVE_LABEL: Record<string, string> = {
  prestamista: 'Prestamista',
  banco: 'Banco',
  familia: 'Familia',
  tarjeta: 'Tarjeta',
  no_resuelve: 'No resuelve',
}

const ORGANICO = 'orgánico'

const fuenteDe = (r: LeadRow) => r.utm_source?.trim() || ORGANICO

/** Teléfono limpio (solo dígitos) con prefijo 502 para wa.me. */
const telWa = (telefono: string) => {
  const digits = telefono.replace(/\D/g, '')
  return digits.length === 8 ? `502${digits}` : digits
}

const fmtFechaHora = (iso: string) =>
  new Date(iso).toLocaleString('es-GT', {
    day: '2-digit', month: 'short',
    hour: '2-digit', minute: '2-digit', hour12: true,
    timeZone: 'America/Guatemala',
  })

/** Cuenta ocurrencias por clave y devuelve entradas ordenadas desc. */
function countBy(rows: LeadRow[], key: (r: LeadRow) => string): Array<[string, number]> {
  const map = new Map<string, number>()
  for (const r of rows) {
    const k = key(r)
    map.set(k, (map.get(k) ?? 0) + 1)
  }
  return [...map.entries()].sort((a, b) => b[1] - a[1])
}

export function NeoCorePresaCrediticiaPage() {
  const [rows, setRows] = useState<LeadRow[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [filtroFuente, setFiltroFuente] = useState('todas')
  const [filtroUrgencia, setFiltroUrgencia] = useState('todas')
  const [exportando, setExportando] = useState(false)
  const [ultimoRefresh, setUltimoRefresh] = useState<Date | null>(null)

  const load = useCallback(async () => {
    const { data, error } = await supabase
      .from('lead_credito')
      .select(`
        id, telefono, nombre, monto_deseado, proposito, urgencia,
        como_resuelve_hoy, posicion, referidos_count,
        utm_source, utm_medium, utm_campaign, utm_content,
        estado, score_declarado, created_at
      `)
      .order('created_at', { ascending: false })
      .limit(2000)
    if (!error) {
      setRows((data ?? []) as LeadRow[])
      setUltimoRefresh(new Date())
    }
    setLoading(false)
  }, [])

  useEffect(() => { void load() }, [load])

  // Auto-refresh cada 60s mientras la página está montada (día de pauta).
  useEffect(() => {
    const t = setInterval(() => { void load() }, 60_000)
    return () => clearInterval(t)
  }, [load])

  // ── KPIs ─────────────────────────────────────────────────────────
  const kpis = useMemo(() => {
    const total = rows.length
    const montoTotal = rows.reduce((acc, r) => acc + Number(r.monto_deseado || 0), 0)
    const hoy = hoyGT()
    const leadsHoy = rows.filter((r) => fechaGT(new Date(r.created_at)) === hoy).length
    const urgenciaYa = rows.filter((r) => r.urgencia === 'ya').length
    const pctYa = total > 0 ? Math.round((urgenciaYa / total) * 100) : 0
    return { total, montoTotal, leadsHoy, pctYa }
  }, [rows])

  // ── Breakdowns ───────────────────────────────────────────────────
  const porProposito = useMemo(
    () => countBy(rows, (r) => PROPOSITO_LABEL[r.proposito] ?? r.proposito), [rows])
  const porUrgencia = useMemo(
    () => countBy(rows, (r) => URGENCIA_LABEL[r.urgencia] ?? r.urgencia), [rows])
  const porFuente = useMemo(() => countBy(rows, fuenteDe), [rows])

  const fuentes = useMemo(
    () => [...new Set(rows.map(fuenteDe))].sort((a, b) => a.localeCompare(b)), [rows])

  // ── Filtros de tabla ─────────────────────────────────────────────
  const filtered = useMemo(() => {
    let list = rows
    if (filtroFuente !== 'todas') list = list.filter((r) => fuenteDe(r) === filtroFuente)
    if (filtroUrgencia !== 'todas') list = list.filter((r) => r.urgencia === filtroUrgencia)
    const q = search.trim().toLowerCase()
    if (q) {
      const qDigits = q.replace(/\D/g, '')
      list = list.filter((r) =>
        (r.nombre ?? '').toLowerCase().includes(q) ||
        (qDigits.length > 0 && r.telefono.replace(/\D/g, '').includes(qDigits)),
      )
    }
    return list
  }, [rows, filtroFuente, filtroUrgencia, search])

  // ── Export Excel ─────────────────────────────────────────────────
  const exportarXlsx = async () => {
    if (exportando || filtered.length === 0) return
    setExportando(true)
    try {
      const config = await fetchConfigReporte()
      const headers = [
        'Fecha', 'Nombre', 'Teléfono', 'Monto deseado', 'Propósito', 'Urgencia',
        'Cómo resuelve hoy', 'Posición', 'Referidos', 'UTM source', 'UTM campaign', 'Estado',
      ]
      const filas = filtered.map((r) => [
        fmtFechaHora(r.created_at),
        r.nombre ?? '—',
        r.telefono,
        formatQ(r.monto_deseado),
        PROPOSITO_LABEL[r.proposito] ?? r.proposito,
        URGENCIA_LABEL[r.urgencia] ?? r.urgencia,
        r.como_resuelve_hoy ? (RESUELVE_LABEL[r.como_resuelve_hoy] ?? r.como_resuelve_hoy) : '—',
        r.posicion,
        r.referidos_count,
        fuenteDe(r),
        r.utm_campaign ?? '—',
        r.estado,
      ])
      const totalRow: (string | number)[] = headers.map(() => '')
      totalRow[2] = 'TOTAL'
      totalRow[3] = formatQ(filtered.reduce((acc, r) => acc + Number(r.monto_deseado || 0), 0))
      await exportarExcel({
        config,
        titulo: 'Presa crediticia · Leads funnel /credito',
        subtitulo: `${filtered.length} leads`,
        headers,
        rows: filas,
        totalRow,
        filename: 'presa-crediticia',
      })
    } finally {
      setExportando(false)
    }
  }

  return (
    <div style={{ padding: '20px 24px', maxWidth: 1300, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 18, gap: 16 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, letterSpacing: '-0.01em' }}>
            Presa crediticia
          </h1>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--muted-foreground)' }}>
            Leads del funnel neocoop.gt/credito · campaña "Decinos cuánto necesitás".
            {ultimoRefresh && (
              <span> Actualizado {ultimoRefresh.toLocaleTimeString('es-GT', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false, timeZone: 'America/Guatemala' })} · refresca solo cada 60s.</span>
            )}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={() => void exportarXlsx()}
            disabled={exportando || filtered.length === 0}
            className="btn btn-ghost"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            <DownloadSimple size={13} weight="bold" /> {exportando ? 'Exportando…' : 'Exportar Excel'}
          </button>
          <button onClick={() => void load()} className="btn btn-ghost" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <ArrowsClockwise size={13} weight="bold" /> Refrescar
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 12 }}>
        <KpiCard label="Total leads" value={String(kpis.total)} />
        <KpiCard label="Monto deseado (suma)" value={formatQ(kpis.montoTotal)} highlight />
        <KpiCard label="Leads de hoy" value={String(kpis.leadsHoy)} />
        <KpiCard label='Urgencia "ya"' value={`${kpis.pctYa}%`} />
      </div>

      {/* Breakdowns */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12, marginBottom: 12 }}>
        <BreakdownCard titulo="Por propósito" entries={porProposito} total={kpis.total} />
        <BreakdownCard titulo="Por urgencia" entries={porUrgencia} total={kpis.total} />
        <BreakdownCard titulo="Por fuente (utm_source)" entries={porFuente} total={kpis.total} />
      </div>

      {/* Filtros + búsqueda */}
      <div style={{
        background: 'var(--surface)', border: '1px solid var(--hairline)',
        borderRadius: 12, padding: '12px 14px', marginBottom: 12,
        display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
      }}>
        <select value={filtroFuente} onChange={(e) => setFiltroFuente(e.target.value)} style={selectStyle}>
          <option value="todas">Fuente · todas</option>
          {fuentes.map((f) => <option key={f} value={f}>{f}</option>)}
        </select>
        <select value={filtroUrgencia} onChange={(e) => setFiltroUrgencia(e.target.value)} style={selectStyle}>
          <option value="todas">Urgencia · todas</option>
          <option value="ya">Ya</option>
          <option value="este_mes">Este mes</option>
          <option value="mas_adelante">Más adelante</option>
        </select>
        <div style={{ flex: 1 }} />
        <div style={{
          display: 'flex', alignItems: 'center', gap: 6,
          background: 'var(--bg)', borderRadius: 8,
          padding: '6px 10px', border: '1px solid var(--hairline)',
          minWidth: 240,
        }}>
          <MagnifyingGlass size={14} weight="bold" color="var(--muted-foreground)" />
          <input
            type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Nombre o teléfono…"
            style={{
              flex: 1, border: 0, background: 'transparent',
              outline: 'none', fontSize: 13, fontFamily: 'inherit',
              color: 'var(--ink)',
            }}
          />
        </div>
      </div>

      {/* Tabla */}
      <div style={{
        background: 'var(--surface)', border: '1px solid var(--hairline)',
        borderRadius: 12, overflow: 'hidden',
      }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted-foreground)' }}>Cargando…</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted-foreground)' }}>
            <Funnel size={28} weight="duotone" /><br />
            {rows.length === 0 ? 'Todavía no cayó ningún lead.' : 'Sin leads con estos filtros.'}
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--hairline)', background: 'var(--bg)' }}>
                  <th style={th}>Fecha</th>
                  <th style={th}>Lead</th>
                  <th style={{ ...th, textAlign: 'right' }}>Monto</th>
                  <th style={th}>Propósito</th>
                  <th style={th}>Urgencia</th>
                  <th style={th}>Resuelve con</th>
                  <th style={{ ...th, textAlign: 'right' }}>Pos.</th>
                  <th style={{ ...th, textAlign: 'right' }}>Ref.</th>
                  <th style={th}>Fuente</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.id} style={{ borderBottom: '1px solid var(--line-soft)' }}>
                    <td style={{ ...td, whiteSpace: 'nowrap', fontFeatureSettings: '"tnum"' }}>
                      {fmtFechaHora(r.created_at)}
                    </td>
                    <td style={td}>
                      <div style={{ fontWeight: 600 }}>{r.nombre ?? '—'}</div>
                      <a
                        href={`https://wa.me/${telWa(r.telefono)}`}
                        target="_blank" rel="noreferrer"
                        style={{ fontSize: 11, color: 'var(--brand-700)', fontWeight: 700, textDecoration: 'none', fontFeatureSettings: '"tnum"' }}
                      >
                        {r.telefono} · WhatsApp
                      </a>
                    </td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 700, fontFeatureSettings: '"tnum"', whiteSpace: 'nowrap' }}>
                      {formatQ(r.monto_deseado)}
                    </td>
                    <td style={td}>{PROPOSITO_LABEL[r.proposito] ?? r.proposito}</td>
                    <td style={td}>
                      <span style={{
                        fontSize: 10, fontWeight: 800, padding: '3px 10px', borderRadius: 99,
                        textTransform: 'uppercase', letterSpacing: 0.5, whiteSpace: 'nowrap',
                        background: r.urgencia === 'ya' ? 'rgba(217,119,6,0.10)' : 'var(--n-100)',
                        color: r.urgencia === 'ya' ? 'var(--warning, #d97706)' : 'var(--ink-soft, #4b5563)',
                      }}>
                        {URGENCIA_LABEL[r.urgencia] ?? r.urgencia}
                      </span>
                    </td>
                    <td style={td}>
                      {r.como_resuelve_hoy ? (RESUELVE_LABEL[r.como_resuelve_hoy] ?? r.como_resuelve_hoy) : '—'}
                    </td>
                    <td style={{ ...td, textAlign: 'right', fontFeatureSettings: '"tnum"' }}>{r.posicion}</td>
                    <td style={{ ...td, textAlign: 'right', fontFeatureSettings: '"tnum"' }}>
                      {r.referidos_count > 0 ? r.referidos_count : '—'}
                    </td>
                    <td style={td}>
                      <div style={{ fontWeight: 600 }}>{fuenteDe(r)}</div>
                      {r.utm_campaign && (
                        <div style={{ fontSize: 11, color: 'var(--muted-foreground)' }}>{r.utm_campaign}</div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

// ── Piezas de UI ───────────────────────────────────────────────────

function KpiCard({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div style={{
      background: 'var(--surface)', border: '1px solid var(--hairline)',
      borderRadius: 12, padding: '14px 16px',
    }}>
      <p style={{ margin: 0, fontSize: 10, fontWeight: 800, color: 'var(--muted-foreground)', textTransform: 'uppercase', letterSpacing: 1.2 }}>
        {label}
      </p>
      <p style={{
        margin: '6px 0 0', fontSize: 24, fontWeight: 800,
        color: highlight ? 'var(--brand)' : 'var(--ink)',
        fontFeatureSettings: '"tnum"', letterSpacing: '-0.01em',
      }}>
        {value}
      </p>
    </div>
  )
}

function BreakdownCard({ titulo, entries, total }: {
  titulo: string
  entries: Array<[string, number]>
  total: number
}) {
  return (
    <div style={{
      background: 'var(--surface)', border: '1px solid var(--hairline)',
      borderRadius: 12, padding: '12px 14px',
    }}>
      <p style={{ margin: '0 0 8px', fontSize: 10, fontWeight: 800, color: 'var(--muted-foreground)', textTransform: 'uppercase', letterSpacing: 1.2 }}>
        {titulo}
      </p>
      {entries.length === 0 ? (
        <p style={{ margin: 0, fontSize: 12, color: 'var(--muted-foreground)' }}>Sin datos.</p>
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {entries.map(([label, n]) => (
            <span key={label} style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '4px 10px', borderRadius: 99,
              background: 'var(--bg)', border: '1px solid var(--hairline)',
              fontSize: 12, fontWeight: 700, color: 'var(--ink-soft, #4b5563)',
            }}>
              {label}
              <span style={{
                fontSize: 10, fontWeight: 800, padding: '1px 6px', borderRadius: 99,
                background: 'var(--n-100)', color: 'var(--muted-foreground)',
                fontFeatureSettings: '"tnum"',
              }}>
                {n}{total > 0 ? ` · ${Math.round((n / total) * 100)}%` : ''}
              </span>
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

const th: React.CSSProperties = {
  textAlign: 'left', padding: '10px 14px',
  fontSize: 10, fontWeight: 800, color: 'var(--muted-foreground)',
  textTransform: 'uppercase', letterSpacing: 1.2,
}
const td: React.CSSProperties = { padding: '12px 14px', verticalAlign: 'top' }

const selectStyle: React.CSSProperties = {
  padding: '7px 10px', borderRadius: 8,
  border: '1px solid var(--hairline)', background: 'var(--bg)',
  fontSize: 12, fontWeight: 700, color: 'var(--ink)',
  fontFamily: 'inherit', cursor: 'pointer',
}
